import React from 'react';
import { useSocket } from '../../contexts/SocketContext';
import { PlayerRole, getLanguage } from '@deadpan/shared';
import { useTranslations } from '../../hooks/useTranslations';
import { useGameWord } from '../../hooks/useGameWord';
import { getRoleConfig } from '../../utils/roleConfig';
import { DeadpanAnnouncementLine } from './DeadpanAnnouncementLine';

export const DeadpanVoting: React.FC = () => {
  const { socket, gameState, myRole, currentRoom, myPlayerId } = useSocket();
  const t = useTranslations();
  const isZh = getLanguage() === 'zh';
  const { word } = useGameWord(gameState);
  const [tempSelection, setTempSelection] = React.useState<string | null>(null);
  
  // Only check for vote if we're in VOTING phase
  const myVote = gameState && myPlayerId && currentRoom?.phase === 'voting' 
    ? gameState.votes[myPlayerId] 
    : undefined;
  // Check if vote exists (not undefined and not null)
  const hasVoted = myVote != null;
  
  React.useEffect(() => {
    // Reset when entering voting phase or when vote is cleared
    if (currentRoom?.phase === 'voting') {
      if (myVote) {
        setTempSelection(myVote);
      } else {
        setTempSelection(null);
      }
    } else {
      setTempSelection(null);
    }
  }, [myVote, currentRoom?.phase]);

  if (!gameState || !currentRoom) {
    return null;
  }

  const isDetective = myRole === PlayerRole.DETECTIVE;
  const isMySpeakingRole = myRole !== PlayerRole.DETECTIVE && myRole !== PlayerRole.WORDSMITH;

  // Everyone except detective and wordsmith; order from server votingOrder (shuffled each voting phase)
  const revealedPlayers = gameState.revealedPlayers || [];
  const allVotablePlayers = currentRoom.players.filter(
    (p) => !p.isSpectator && p.role !== PlayerRole.DETECTIVE && p.role !== PlayerRole.WORDSMITH,
  );
  const votingOrder = gameState.votingOrder || [];
  const spokenPlayerIds = gameState.deadpanSpokenPlayerIds || [];
  const myHasSpoken = myPlayerId ? spokenPlayerIds.includes(myPlayerId) : false;
  const canFinishSpeaking = currentRoom.phase === 'voting' && isMySpeakingRole && !myHasSpoken;
  const votablePlayers = React.useMemo(() => {
    if (votingOrder.length > 0) {
      const ordered = votingOrder
        .map((playerId) => allVotablePlayers.find((p) => p.id === playerId))
        .filter((p): p is NonNullable<typeof p> => p !== undefined);
      const notInVotingOrder = allVotablePlayers.filter((p) => !votingOrder.includes(p.id));
      return [...ordered, ...notInVotingOrder];
    }
    return [...allVotablePlayers].sort((a, b) => {
      const indexA = currentRoom.players.findIndex((p) => p.id === a.id);
      const indexB = currentRoom.players.findIndex((p) => p.id === b.id);
      return indexA - indexB;
    });
  }, [votingOrder, allVotablePlayers, currentRoom.players]);

  const roleConfig = getRoleConfig(t);

  const handleSelect = (playerId: string) => {
    if (!hasVoted) setTempSelection(playerId);
  };

  const handleConfirmVote = () => {
    if (socket && tempSelection && !hasVoted) {
      socket.emit('client:submit-vote', tempSelection);
    }
  };

  return (
    <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-4 sm:p-6 md:p-8 w-[90%] max-w-[600px] border border-white/20 flex flex-col items-center">
      <div className="w-full text-center mb-4 sm:mb-6 md:mb-8">
        <h2 className="text-base sm:text-lg font-semibold text-gray-700 mb-2 px-2">
          {t.games.deadpan.theWord}
        </h2>
        <div className="w-full overflow-x-auto">
          <p className="text-[clamp(1.5rem,8vw,3rem)] font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mb-2 px-2 whitespace-nowrap">
            {word}
          </p>
        </div>
        {isDetective && (
          <p className="text-base sm:text-lg text-gray-700 mb-2 sm:mb-4 px-2">{t.games.deadpan.detectiveGoal}</p>
        )}
        <p className="text-xs sm:text-sm text-gray-500 px-2">
          {isDetective 
            ? (hasVoted ? t.games.deadpan.voteSubmitted : tempSelection ? t.games.deadpan.confirmYourVote : t.games.deadpan.selectTruthTeller)
            : t.games.deadpan.waitingForDetectivesToVote
          }
        </p>
        <DeadpanAnnouncementLine />
      </div>

      <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 mb-4 sm:mb-6">
        {votablePlayers.map((player) => {
          const isRevealed = revealedPlayers.includes(player.id);
          const voteCount = Object.values(gameState.votes).filter(id => id === player.id).length;
          const isSelected = isDetective && tempSelection === player.id;
          const hasSpoken = spokenPlayerIds.includes(player.id);
          
          // If revealed, show as flipped card with role
          if (isRevealed && player.role) {
            const config = roleConfig[player.role];
            return (
              <div
                key={player.id}
                className={`p-4 sm:p-6 rounded-xl border-2 bg-gradient-to-r ${config.gradient} text-white`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg sm:text-xl font-bold">{player.name}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xl sm:text-2xl">{config.emoji}</span>
                      <p className="text-sm sm:text-base opacity-90">{config.name}</p>
                    </div>
                  </div>
                </div>
              </div>
            );
          }
          
          // If not revealed, show as votable button/div
          const Component = isDetective ? 'button' : 'div';
          const componentProps = isDetective 
            ? {
                onClick: () => !hasVoted && handleSelect(player.id),
                disabled: hasVoted,
              }
            : {};
          
          return (
            <Component
              key={player.id}
              {...componentProps}
              className={`p-4 sm:p-6 rounded-xl border-2 transition-all text-left ${
                isSelected
                  ? 'border-blue-500 bg-blue-50 shadow-lg'
                  : hasSpoken
                  ? 'border-emerald-300 bg-emerald-50'
                  : hasVoted || !isDetective
                  ? 'border-gray-200 bg-gray-50 opacity-60'
                  : 'border-gray-200 bg-white hover:border-blue-300 hover:shadow-md'
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-800">{player.name}</h3>
                  {hasSpoken && (
                    <p className="text-xs sm:text-sm text-emerald-700 mt-1 font-semibold">
                      {isZh ? '已结束发言' : 'Finished speaking'}
                    </p>
                  )}
                  {voteCount > 0 && (
                    <p className="text-xs sm:text-sm text-gray-600 mt-1">
                      {voteCount} {voteCount === 1 ? t.games.deadpan.vote : t.games.deadpan.votes}
                    </p>
                  )}
                </div>
                {isSelected && (
                  <span className="text-xl sm:text-2xl">✓</span>
                )}
              </div>
            </Component>
          );
        })}
      </div>

      {isMySpeakingRole && (
        <div className="w-full mb-4 sm:mb-6">
          {canFinishSpeaking ? (
            <button
              type="button"
              onClick={() => socket?.emit('client:deadpan-finish-speaking')}
              className="w-full px-3 py-3 rounded-xl bg-gradient-to-r from-sky-600 to-blue-600 text-white font-semibold hover:from-sky-700 hover:to-blue-700"
            >
              {isZh ? '结束发言' : 'Finish Speaking'}
            </button>
          ) : (
            <div className="rounded-xl border border-sky-200 bg-sky-50 px-3 py-3 text-sm font-semibold text-sky-800 text-center">
              {isZh ? '已结束发言' : 'Finished speaking'}
            </div>
          )}
        </div>
      )}

      {isDetective && !hasVoted && tempSelection && (
        <div className="w-full mb-4 sm:mb-6">
          <button
            onClick={handleConfirmVote}
            className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 sm:py-4 px-4 sm:px-6 rounded-xl font-bold text-base sm:text-lg hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
          >
            {t.games.deadpan.confirmVote}
          </button>
        </div>
      )}

    </div>
  );
};

